import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatStepperModule } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { debounceTime, distinctUntilChanged, Subject, catchError, of } from 'rxjs';
import { SupplierService } from '../../../../core/services/supplier.service';
import { QuotaService } from '../../services/quota.service';
import { extractApiError, formatApiDate } from '../../../../core/utils/api-format';

interface LegalStatus {
  id: number;
  nameRu: string;
  nameKy: string;
  shortNameRu?: string;
}

@Component({
  selector: 'app-supplier-registration',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule
  ],
  templateUrl: './supplier-registration.html',
  styleUrl: './supplier-registration.css',
})
export class SupplierRegistrationComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private supplierService = inject(SupplierService);
  private quotaService = inject(QuotaService);

  // Сигналы для состояния
  loading = signal(false);
  loadingForm = signal(false);
  errorMessage = signal<string | null>(null);
  currentStep = signal(0);
  applicantType = signal<string>('');
  actingAs = signal<string>('');

  // Справочники
  legalStatuses = signal<LegalStatus[]>([]);

  // Subject для debounce ИНН
  private innChange$ = new Subject<string>();

  // Формы для каждого шага
  step1Form!: FormGroup;
  step2Form!: FormGroup;

  // Опции для селектов
  passportTypes = [
    { value: 'ID', label: 'ID' },
    { value: 'AN', label: 'AN' },
    { value: 'PN', label: 'PN' }
  ];



  actingAsOptions = [
    { value: 'AGENT', label: 'Агент' },
    { value: 'DIRECTOR', label: 'Директор' }
  ];

  ngOnInit(): void {
    this.initForms();
    this.loadLegalStatuses();
    this.setupInnDebounce();
  }

  private initForms(): void {
    // Форма шага 1 - информация о компании
    this.step1Form = this.fb.group({
      inn: ['', [Validators.required, Validators.pattern(/^\d{14}$/)]],
      companyName: ['', Validators.required],
      okpo: [''],
      legalStatusId: [null],
      registrationNumber: ['', Validators.required],
      registrationDate: [null, Validators.required],
      supplierType: ['HISTORICAL_SUPPLIER', Validators.required]
    });

    // Форма шага 2 - контактная информация
    this.step2Form = this.fb.group({
      actingAs: ['', Validators.required],
      contractNumber: [''],
      contractDate: [null]
    });

    // Подписка на изменение actingAs
    this.step2Form.get('actingAs')?.valueChanges.subscribe(value => {
      this.actingAs.set(value);
      if (value !== 'AGENT') {
        this.step2Form.patchValue({
          contractNumber: '',
          contractDate: null
        });
      }
    });
  }

  private setupInnDebounce(): void {
    this.innChange$.pipe(
      debounceTime(600),
      distinctUntilChanged()
    ).subscribe(inn => {
      if (inn && inn.startsWith('0') && inn.length >= 14) {
        this.fetchCompanyDetails(inn);
      }
    });
  }

  private loadLegalStatuses(): void {
    this.quotaService.getLegalStatuses()
      .pipe(
        catchError(() => of({ res: { content: [] } }))
      )
      .subscribe(response => {
        const statuses = response?.res?.content || response?.res || [];
        this.legalStatuses.set(statuses);
      });
  }

  onInnChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '').slice(0, 14);
    input.value = value;
    this.step1Form.patchValue({ inn: value });

    // Определяем тип заявителя
    let detectedType = '';
    if (value) {
      detectedType = value.startsWith('0')
        ? 'LIMITED_LIABILITY_COMPANY'
        : 'INDIVIDUAL_ENTREPRENEUR';
    }
    this.applicantType.set(detectedType);

    // Очищаем поля для ИП
    if (detectedType === 'INDIVIDUAL_ENTREPRENEUR') {
      this.step1Form.patchValue({
        okpo: '',
        legalStatusId: null
      });
      this.step2Form.patchValue({
        actingAs: '',
        contractNumber: '',
        contractDate: null
      });
      this.actingAs.set('');
    }

    // Если ИНН пустой - сбрасываем всё
    if (!value) {
      this.applicantType.set('');
      this.step1Form.patchValue({
        okpo: '',
        legalStatusId: null
      });
    }

    // Запускаем debounce для загрузки данных компании
    this.innChange$.next(value);
  }

  private fetchCompanyDetails(inn: string): void {
    this.loadingForm.set(true);

    this.quotaService.getOrganizationByInn(inn).subscribe({
      next: (response) => {
        const company = response?.res?.content?.[0];

        if (company) {
          const suffixRaw = this.getNumberSuffix(company.number);
          const matched = this.findMatchingStatus(suffixRaw);

          this.step1Form.patchValue({
            companyName: company.fullName?.ru || company.fullName?.ky || '',
            registrationNumber: company.number || '',
            okpo: company.statNo || '',
            registrationDate: company.orderDate ? new Date(company.orderDate) : null,
            legalStatusId: matched?.id || null
          });
        }
      },
      error: (err) => {
        console.error('Ошибка при получении данных компании:', err);
        // в т.ч. 429 (лимит запросов к Минюсту) и «Минюст недоступен»
        this.errorMessage.set(extractApiError(err?.error, 'Не удалось получить данные организации'));
        this.loadingForm.set(false);
      },
      complete: () => {
        this.loadingForm.set(false);
      }
    });
  }

  private getNumberSuffix(num?: string): string {
    if (!num) return '';
    const parts = String(num).split(/[-‐‑–—−]/);
    return parts.length >= 3 ? parts.slice(2).join('-') : '';
  }

  private findMatchingStatus(suffixRaw: string): LegalStatus | undefined {
    const norm = (s: string) => (s ?? '')
      .replace(/\s+/g, '')
      .replace(/[-‐‑–—−]/g, '-')
      .replace(/Ё/g, 'Е')
      .toUpperCase();

    const stripHyphens = (s: string) => norm(s).replace(/-/g, '');
    const nSuffix = norm(suffixRaw);
    const nSuffixNH = stripHyphens(suffixRaw);

    return this.legalStatuses().find(st => {
      const shortNorm = norm(st.shortNameRu || '');
      const shortNormNH = stripHyphens(st.shortNameRu || '');
      const nameNorm = norm(st.nameRu || '');
      const nameNormNH = stripHyphens(st.nameRu || '');

      return shortNorm === nSuffix ||
        shortNormNH === nSuffixNH ||
        nameNorm === nSuffix ||
        nameNormNH === nSuffixNH;
    });
  }

  onPassportNumberChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(0, 7);
    input.value = value;
    this.step1Form.patchValue({ passportNumber: value });
  }

  goBack(): void {
    this.router.navigate(['/']);
  }

  goNext(): void {
    if (this.step1Form.valid) {
      this.currentStep.set(1);
    } else {
      this.step1Form.markAllAsTouched();
    }
  }

  goPrev(): void {
    this.currentStep.set(0);
  }

  handleRegistration(): void {
    // Валидация формы в зависимости от типа
    if (this.applicantType() === 'LIMITED_LIABILITY_COMPANY' && this.currentStep() === 1) {
      // Проверяем обязательность полей доверенности
      if (this.actingAs() === 'AGENT') {
        this.step2Form.get('contractNumber')?.setValidators([Validators.required]);
        this.step2Form.get('contractDate')?.setValidators([Validators.required]);
      } else {
        this.step2Form.get('contractNumber')?.clearValidators();
        this.step2Form.get('contractDate')?.clearValidators();
      }
      this.step2Form.get('contractNumber')?.updateValueAndValidity();
      this.step2Form.get('contractDate')?.updateValueAndValidity();

      if (!this.step2Form.valid) {
        this.step2Form.markAllAsTouched();
        return;
      }
    }

    if (!this.step1Form.valid) {
      this.step1Form.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);

    const formData = this.buildRegistrationData();

    this.quotaService.registerSupplier(formData).subscribe({
      next: (response) => {
        if (response?.success) {
          this.supplierService.setRegistered(response.res);
          this.router.navigate(['/']);
        } else {
          const errorMsg = extractApiError(response, 'Ошибка регистрации');
          this.errorMessage.set(errorMsg);
        }
      },
      error: (error: any) => {
        console.error('Ошибка регистрации:', error);
        const errorMsg = extractApiError(error?.error, 'Произошла ошибка при регистрации');
        this.errorMessage.set(errorMsg);
        this.loading.set(false);
      },
      complete: () => {
        this.loading.set(false);
      }
    });
  }

  private buildRegistrationData(): any {
    const step1 = this.step1Form.value;
    const step2 = this.step2Form.value;

    const data: any = {
      inn: step1.inn,
      companyName: step1.companyName,
      registrationNumber: step1.registrationNumber,
      registrationDate: this.formatDate(step1.registrationDate),
      applicantTypeEnum: this.applicantType()
    };

    // Поля для ООО
    if (this.applicantType() === 'LIMITED_LIABILITY_COMPANY') {
      data.okpo = step1.okpo;

      // Находим выбранный юридический статус
      if (step1.legalStatusId) {
        const selectedStatus = this.legalStatuses().find(s => s.id === step1.legalStatusId);
        if (selectedStatus) {
          data.legalStatus = {
            id: selectedStatus.id,
            nameRu: selectedStatus.nameRu,
            nameKy: selectedStatus.nameKy
          };
        }
      }

      data.actingAs = step2.actingAs;

      if (step2.actingAs === 'AGENT') {
        data.contractNumber = step2.contractNumber;
        data.contractDate = this.formatDate(step2.contractDate);
        data.proxyDocumentNumber = null;
        data.proxyDocumentDate = null;
      }
    }

    return data;
  }

  private formatDate(date: Date | null): string | null {
    if (!date) return null;
    return formatApiDate(date);
  }

  // Геттеры для шаблона
  isLLC(): boolean {
    return this.applicantType() === 'LIMITED_LIABILITY_COMPANY';
  }

  isIE(): boolean {
    return this.applicantType() === 'INDIVIDUAL_ENTREPRENEUR';
  }

  showAgentFields(): boolean {
    return this.actingAs() === 'AGENT';
  }
}
